import {
  type BillingQuery,
  type BillingStateQuery,
  DownPaymentPreset,
  type InvoicesQuery,
  InvoiceType,
  SupplierBillingStateSaleStatus,
} from "~/graphql/graphql-new.gen";

export const billingPreviews = [
  "upcoming",
  "ongoing",
  "delivery",
  "claim",
  "return",
  "withdrawal",
  "default",
  "classic-rules",
  "classic-upload",
  "exceptional",
  "unlocked",
  "bypassed",
] as const;

export type BillingPreview = (typeof billingPreviews)[number];

export const billingViews = ["brand", "choose"] as const;

export type BillingView = (typeof billingViews)[number];

const withdrawalPeriodEndDate = new Date(
  Date.now() + 14 * 24 * 60 * 60 * 1000,
).toISOString();

export const parseBillingPreview = (
  value: string | null,
): BillingPreview | undefined =>
  billingPreviews.find((preview) => preview === value);

export const getBillingPreview = (): BillingPreview | undefined =>
  import.meta.env.DEV
    ? parseBillingPreview(
        new URLSearchParams(window.location.search).get("billing-preview"),
      )
    : undefined;

export const parseBillingView = (
  value: string | null,
): BillingView | undefined => billingViews.find((view) => view === value);

export const getBillingView = (): BillingView | undefined =>
  import.meta.env.DEV
    ? parseBillingView(new URLSearchParams(window.location.search).get("view"))
    : undefined;

export const applyBillingView = (
  isInternalUser: boolean,
  view: BillingView | undefined,
): boolean => (view ? view === "choose" : isInternalUser);

export const applyBillingStatePreview = (
  state: BillingStateQuery["supplierBillingState"],
  preview: BillingPreview | undefined,
): BillingStateQuery["supplierBillingState"] => {
  if (!preview) return state;

  const lockedState = {
    ...state,
    canAccess: false,
    canAccessBypassed: false,
    isPendingClaimClosing: false,
    isPendingOrderDelivery: false,
    isPendingReturnValidation: false,
    isPendingWithdrawalPeriod: false,
    pendingClaimClosingOrderIds: [],
    pendingOrderDeliveryOrderIds: [],
    pendingReturnValidationOrderIds: [],
    saleStatus: SupplierBillingStateSaleStatus.Ended,
    withdrawalPeriodEndDate,
  };

  switch (preview) {
    case "upcoming":
      return {
        ...lockedState,
        saleStatus: SupplierBillingStateSaleStatus.Upcoming,
      };
    case "ongoing":
    case "classic-rules":
    case "classic-upload":
    case "exceptional":
      return {
        ...lockedState,
        saleStatus: SupplierBillingStateSaleStatus.Ongoing,
      };
    case "delivery":
      return {
        ...lockedState,
        isPendingOrderDelivery: true,
        isPendingWithdrawalPeriod: true,
        pendingOrderDeliveryOrderIds: ["preview-delivery"],
      };
    case "claim":
      return {
        ...lockedState,
        isPendingClaimClosing: true,
        pendingClaimClosingOrderIds: ["preview-claim"],
      };
    case "return":
      return {
        ...lockedState,
        isPendingReturnValidation: true,
        pendingReturnValidationOrderIds: ["preview-return"],
      };
    case "withdrawal":
      return {
        ...lockedState,
        isPendingWithdrawalPeriod: true,
      };
    case "unlocked":
      return { ...lockedState, canAccess: true };
    case "bypassed":
      return {
        ...lockedState,
        canAccessBypassed: true,
        isPendingOrderDelivery: true,
        isPendingWithdrawalPeriod: true,
        pendingOrderDeliveryOrderIds: ["preview-delivery"],
      };
    default:
      return lockedState;
  }
};

export const applyBillingPreview = (
  billing: BillingQuery["billingV2"],
  preview: BillingPreview | undefined,
): BillingQuery["billingV2"] => {
  if (!preview) return billing;

  if (preview === "exceptional") {
    return {
      ...billing,
      down_payment: {
        __typename: "DownPaymentExceptional",
        preset: DownPaymentPreset.Exceptional,
      },
    };
  }

  if (preview !== "classic-rules" && preview !== "classic-upload") {
    return { ...billing, down_payment: null };
  }

  const rulesAreValidated = preview === "classic-upload";

  return {
    ...billing,
    down_payment: {
      __typename: "DownPaymentClassic",
      preset: DownPaymentPreset.Classic,
      rate: 30,
      financialDetail: {
        agg_by_vat_rate: billing.agg_by_vat_rate,
        total_billing_ht: billing.total_billing_ht,
        total_billing_ttc: billing.total_billing_ttc,
        total_commission_ht: billing.total_commission_ht,
        total_commission_ttc: billing.total_commission_ttc,
        total_delivery_ht: billing.total_delivery_ht,
        total_delivery_ttc: billing.total_delivery_ttc,
        total_operation_ht: billing.total_operation_ht,
        total_operation_ttc: billing.total_operation_ttc,
        total_refund_ht: billing.total_refund_ht,
        total_refund_ttc: billing.total_refund_ttc,
        total_remaining_ht: billing.total_remaining_ht,
        total_remaining_ttc: billing.total_remaining_ttc,
      },
      rules: [
        {
          __typename: "FulfilledOrderPercentageRule",
          currentValue: rulesAreValidated ? 80 : 50,
          isValidated: rulesAreValidated,
          targetValue: 80,
        },
        {
          __typename: "ActivationDateRule",
          activationDate: withdrawalPeriodEndDate,
          isValidated: rulesAreValidated,
        },
      ],
    },
  };
};

export const applyInvoicesPreview = (
  invoices: InvoicesQuery["invoices"],
  preview: BillingPreview | undefined,
): InvoicesQuery["invoices"] =>
  preview === "classic-rules" ||
  preview === "classic-upload" ||
  preview === "exceptional"
    ? invoices.filter((invoice) => invoice.type !== InvoiceType.DownPayment)
    : invoices;
